import * as React from 'react';
import { Box } from '@mui/material';
import { tokens } from '../DashboardTheme/DashboardTheme';
import { Kicker, BodyText } from '../Typography/Typography';
import Reveal from '../Reveal/Reveal';

/**
 * Shared layout for the three training pages (Bag / Pao / Sparring):
 * a full-bleed image header with the discipline name, followed by a
 * single reading column of sections.
 *
 * The header used to autoplay a clip; the smallest of those was 4 MB and the
 * sparring one 17 MB, for a decorative background. It is a still now, sized to
 * the header ratio so nothing gets cropped.
 */
export default function DisciplinePage({ kicker, title, intro, sections, image }) {
    return (
        <Box sx={{ backgroundColor: tokens.ink, minHeight: '100vh' }}>
            {/* ---------- Media header ---------- */}
            <Box
                sx={{
                    position: 'relative',
                    aspectRatio: { xs: '1 / 1', md: '2 / 1' },
                    // Capped so the header never scales the photo above its own
                    // 1440px: full-bleed was upscaling it on wide screens, which
                    // is what made it look soft.
                    maxWidth: 1440,
                    mx: 'auto',
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'flex-end',
                }}
            >
                <Box
                    component="img"
                    src={image}
                    alt={title}
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        filter: 'brightness(0.5) contrast(1.05)',
                    }}
                />

                <Box
                    aria-hidden
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        background: `linear-gradient(180deg, ${tokens.ink}B3 0%, transparent 35%, ${tokens.ink} 100%)`,
                    }}
                />

                <Box
                    sx={{
                        position: 'relative',
                        width: '100%',
                        maxWidth: 780,
                        mx: 'auto',
                        px: { xs: 3, md: 6 },
                        pb: { xs: 5, md: 8 },
                    }}
                >
                    <Kicker justifyContent="flex-start">{kicker}</Kicker>
                    <Box
                        component="h1"
                        sx={{
                            m: 0,
                            mt: 1.5,
                            fontFamily: tokens.display,
                            fontSize: { xs: 62, sm: 96, md: 140 },
                            lineHeight: 0.9,
                            color: tokens.white,
                        }}
                    >
                        {title}
                    </Box>
                </Box>
            </Box>

            {/* ---------- Reading column ---------- */}
            <Box sx={{ maxWidth: 780, mx: 'auto', px: { xs: 3, md: 6 }, py: { xs: 6, md: 10 } }}>
                <Reveal>
                    <BodyText
                        textAlign="left"
                        maxWidth="100%"
                        color={tokens.white}
                        fontSize={{ xs: 17, md: 20 }}
                        sx={{ fontWeight: 300, lineHeight: 1.7 }}
                    >
                        {intro}
                    </BodyText>
                </Reveal>

                {sections.map((section, i) => (
                    <Reveal key={section.heading} delay={i * 80}>
                        <Box sx={{ mt: { xs: 5, md: 7 } }}>
                            <Box
                                sx={{
                                    fontFamily: tokens.heading,
                                    fontWeight: 700,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.06em',
                                    fontSize: { xs: 24, md: 30 },
                                    lineHeight: 1.2,
                                    color: tokens.white,
                                }}
                            >
                                {section.heading}
                            </Box>
                            <Box
                                aria-hidden
                                sx={{
                                    width: 48,
                                    height: 2,
                                    backgroundColor: tokens.red,
                                    my: 2,
                                }}
                            />
                            <BodyText textAlign="left" maxWidth="100%">
                                {section.body}
                            </BodyText>
                        </Box>
                    </Reveal>
                ))}
            </Box>
        </Box>
    );
}
